import { Link } from "react-router-dom";
import { Trash2, AlertTriangle, CheckCircle2, Clock, Ban, Smartphone } from "lucide-react";
import { fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { KIND_HINT, KIND_LABELS } from "@/components/alerts/CreateAlertForm";

const STATUS = {
  active: ["Watching · live + every 15 min", "bg-cyan-500/10 text-cyan-300", AlertTriangle, "bg-amber-500/10 border-amber-900/50 text-amber-400"],
  triggered: ["Triggered", "bg-emerald-500/15 text-emerald-300", CheckCircle2, "bg-emerald-500/10 border-emerald-900/50 text-emerald-400"],
  expired: ["Expired", "bg-slate-800 text-slate-400", Clock, "bg-slate-800/60 border-slate-700 text-slate-400"],
  cancelled: ["Cancelled (OCO)", "bg-slate-800 text-slate-400", Ban, "bg-slate-800/60 border-slate-700 text-slate-400"],
};

export const AlertRow = ({ a, onDelete, highlight, notification }) => {
  const [label, badge, Icon, iconCls] = STATUS[a.status] || STATUS.active;
  const when = a.triggered_at || a.expired_at || a.cancelled_at;
  return (
    <div data-testid={`alert-${a.id}`} className={`flex items-center gap-3 px-4 py-3 ${highlight ? "bg-cyan-500/5 ring-1 ring-inset ring-cyan-800/60" : ""}`}>
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${iconCls}`}><Icon className="w-4 h-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${a.coin_id}`} className="hover:text-cyan-300" data-testid={`alert-coin-link-${a.id}`}>{a.symbol?.toUpperCase()}</Link>
          <Hint k={KIND_HINT[a.kind]} className="inline-flex cursor-help">
            <span className="text-slate-400 font-mono-ticker text-xs">{a.condition || KIND_LABELS[a.kind] || a.kind}</span>
          </Hint>
          {a.group === "bracket" && (
            <Hint k="bracket_alerts" className="inline-flex cursor-help">
              <span data-testid={`alert-bracket-${a.id}`} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${a.role === "stop" ? "bg-red-500/10 text-red-300 border-red-900/60" : "bg-emerald-500/10 text-emerald-300 border-emerald-900/60"}`}>Bracket {a.role}</span>
            </Hint>
          )}
          <span data-testid={`alert-status-${a.status}-${a.id}`} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${badge}`}>
            {label}{a.status === "triggered" && a.triggered_price ? ` @ ${fmtUsd(a.triggered_price)}` : ""}
          </span>
          {notification?.push_status?.state === "sent" && <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1" data-testid={`alert-pushed-${a.id}`}><Smartphone className="w-3 h-3" /> sent to phone</span>}
          {notification && notification.push_status?.state !== "sent" && <span className="text-[10px] text-slate-500" data-testid={`alert-not-pushed-${a.id}`}>in-app only{notification.push_status?.reason ? ` (${notification.push_status.reason.replace("_", " ")})` : ""}</span>}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {a.note && <span>{a.note} · </span>}
          {a.status === "active" && a.expires_at && <span>expires {new Date(a.expires_at).toLocaleDateString()}</span>}
          {when && <span>{a.status} {new Date(when).toLocaleString()}{a.triggered_by ? ` · ${a.triggered_by}` : ""}</span>}
        </div>
      </div>
      {onDelete && <button data-testid={`delete-alert-${a.id}`} onClick={() => onDelete(a.id)} className="btn-ghost rounded-md p-2" aria-label="Delete alert"><Trash2 className="w-4 h-4 text-slate-400" /></button>}
    </div>
  );
};
